import { useCallback, useEffect } from 'react';

import useAuthorization from '@/modules/user/hooks/useAuthorization';
import Token from '@/utils/handlers/Token';

const IMMEDIATE_LOGOUT_EVENT = 'immediate-logout-event';

interface Props {
  isLoggedIn?: boolean;
}

const useSsoLogout = ({ isLoggedIn }: Props) => {
  const { handleLogout } = useAuthorization();

  const syncLogout = useCallback((event: StorageEvent) => {
    if (!isLoggedIn) {
      return;
    }

    if (!Token.getSSODataFromHash() && event.key === IMMEDIATE_LOGOUT_EVENT) {
      handleLogout();
      return;
    }
  }, [isLoggedIn, handleLogout]);

  const checkLogoutRequired = useCallback(() => {
    const ssoData = Token.getSSODataFromHash();
    if (ssoData) {
      localStorage.setItem(IMMEDIATE_LOGOUT_EVENT, 'true');
      handleLogout(() => Token.setSSOToken(ssoData?.accessToken, ssoData?.expiresIn));
      return;
    }
  }, [handleLogout]);

  useEffect(() => {
    checkLogoutRequired();
    window.addEventListener('storage', syncLogout);
    return () => {
      window.removeEventListener('storage', syncLogout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
};

export default useSsoLogout;
